import React, { useEffect, useMemo, useState } from 'react';
import {
    Modal,
    Checkbox,
    Space,
    Typography,
    Spin,
    Collapse,
    Button
} from 'antd';
import { getAllLEs, searchVrUsersForSelector } from 'services/api/vrUsersService';
import { UserSelect, UserSelectByEmail } from 'shared/components/elements/user-select';
import { useSessionsReportData } from 'shared/hooks';
import { getLeById } from 'utils/columns';
import { getSortQueryParams } from 'utils/sort-utils';
import { VrUserInternal } from 'shared/types';

const { Title } = Typography;

const SELECT_USERS_LIMIT = 5000;

type ColumnItem = {
    title: string;
    key: string;
};

type LearningExperience = {
    Key: string;
    name: string;
    stationCount: number;
    hasPracticeProblems?: boolean;
    dataSource?: string | null;
};

type Props = {
    mode: '1' | '2' | '3';
    open: boolean;
    columns: ColumnItem[];
    onClose: () => void;
    initialUserIds?: string[];
    /** When provided, users for step 2 are fetched from users/select with these filters (e.g. from report page filters). */
    selectFilters?: Record<string, string>;
    onExport?: (payload: {
        columns: string[];
        userIds?: string;
    }) => void;
};

const ReportColumnsModal: React.FC<Props> = ({
    mode,
    open,
    columns,
    onClose,
    onExport,
    initialUserIds,
    selectFilters
}): JSX.Element => {
    const [step, setStep] = useState<1 | 2>(1);

    const { fetchVrUsers, vrUsers } = useSessionsReportData();

    const [selectUsers, setSelectUsers] = useState<VrUserInternal[]>([]);
    const [loadingSelectUsers, setLoadingSelectUsers] = useState(false);

    useEffect(() => {
        if (!selectFilters) {
            fetchVrUsers();
        }
    }, [fetchVrUsers, selectFilters]);

    useEffect(() => {
        if (!open || !selectFilters) return;
        setLoadingSelectUsers(true);
        const orderBy = 'email,ASC';
        const params = {
            ...getSortQueryParams(selectFilters),
            isStudent: true,
        };
        searchVrUsersForSelector(orderBy, params, 1, SELECT_USERS_LIMIT)
            .then(res => {
                const list = Array.isArray(res.data) ? res.data : [];
                setSelectUsers(list.map(u => ({ ...u, ID: u.id })));
            })
            .catch(() => setSelectUsers([]))
            .finally(() => setLoadingSelectUsers(false));
    }, [open, selectFilters]);

    const baseColumns = useMemo(
        () => columns.filter(col => !String(col.key).includes('_')),
        [columns]
    );

    const [les, setLes] = useState<LearningExperience[]>([]);
    const [loadingLe, setLoadingLe] = useState(false);

    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [userIds, setUserIds] = useState<string[]>([]);
    const [userEmailIds, setUserEmailIds] = useState<string[]>([]);
    const [isAllUsersSelected, setIsAllUsersSelected] = useState(false);

    useEffect(() => {
        if (!open) return;

        setStep(1);
        setSelectedColumns([]);

        // Always start user selection empty on open.
        // "Select All Users" + backend logic will handle the "all users" case.
        setUserIds([]);
        setUserEmailIds([]);
        setIsAllUsersSelected(false);

        setLoadingLe(true);
        getAllLEs()
            // Already Product D only and in display order (sort_order) from the backend.
            .then(res => setLes(res.data))
            .finally(() => setLoadingLe(false));
    }, [open]);

    const toggleColumn = (key: string) => {
        setSelectedColumns(prev =>
            prev.includes(key)
                ? prev.filter(k => k !== key)
                : [...prev, key]
        );
    };

    const mergeUserIds = (ids: string[], emailIds: string[], allSelected: boolean) => {
        // When "Select All Users" is active, we do NOT send userIds at all.
        // Backend treats absence of userIds as "all users".
        if (allSelected) {
            return undefined;
        }

        const merged = Array.from(new Set([...ids, ...emailIds]));
        return merged.length ? merged.join(',') : undefined;
    };

    const isIndeterminate =
        userIds.length > 0 && !isAllUsersSelected;

    const handleExport = () => {
        const userIdsPayload = mergeUserIds(userIds, userEmailIds, isAllUsersSelected);

        onExport?.({
            columns: selectedColumns,
            userIds: userIdsPayload
        });
        onClose();
    };

    const getLeColumnKeys = (le: LearningExperience): string[] => [
        `${le.Key}_stationsCompleted`,
        `${le.Key}_totalTime`,
        ...(le.hasPracticeProblems ? [`${le.Key}_extraPractice`] : []),
        ...Array.from({ length: le.stationCount }).map(
            (_, idx) => `${le.Key}_st${idx}`
        )
    ];

    const getAllLeColumnKeys = (): string[] =>
        les.flatMap(le => getLeColumnKeys(le));

    const isLeAllSelected = (le: LearningExperience) =>
        getLeColumnKeys(le).every(k => selectedColumns.includes(k));

    const isLeIndeterminate = (le: LearningExperience) => {
        const keys = getLeColumnKeys(le);
        const selected = keys.filter(k => selectedColumns.includes(k));
        return selected.length > 0 && selected.length < keys.length;
    };

    const toggleLeAll = (le: LearningExperience, checked: boolean) => {
        const keys = getLeColumnKeys(le);

        setSelectedColumns(prev =>
            checked
                ? Array.from(new Set([...prev, ...keys]))
                : prev.filter(k => !keys.includes(k))
        );
    };

    const isAllLeSelected = () => {
        const keys = getAllLeColumnKeys();
        return keys.length > 0 && keys.every(k => selectedColumns.includes(k));
    };

    const isAllLeIndeterminate = () => {
        const keys = getAllLeColumnKeys();
        const selected = keys.filter(k => selectedColumns.includes(k));
        return selected.length > 0 && selected.length < keys.length;
    };

    const toggleAllLe = (checked: boolean) => {
        const keys = getAllLeColumnKeys();

        setSelectedColumns(prev =>
            checked
                ? Array.from(new Set([...prev, ...keys]))
                : prev.filter(k => !keys.includes(k))
        );
    };

    return (
        <Modal
            open={open}
            title={step === 1 ? 'Select Report Columns' : 'Select Users'}
            onCancel={onClose}
            width={750}
            footer={
                <Space>
                    {step === 2 && <Button onClick={() => setStep(1)}>Back</Button>}
                    <Button onClick={onClose}>Cancel</Button>
                    {step === 1 && (
                        <Button
                            type="primary"
                            onClick={() => setStep(2)}
                            disabled={!selectedColumns.length}
                        >
                            Next
                        </Button>
                    )}
                    {step === 2 && (
                        <Button type="primary" onClick={handleExport}>
                            Export
                        </Button>
                    )}
                </Space>
            }
        >
            {step === 1 && (
                <>
                    <Title level={5}>Base columns</Title>
                    <Space direction="vertical">
                        {baseColumns.map(col => (
                            <Checkbox
                                key={col.key}
                                checked={selectedColumns.includes(col.key)}
                                onChange={() => toggleColumn(col.key)}
                            >
                                {col.title}
                            </Checkbox>
                        ))}
                    </Space>

                    {mode !== '2' && (
                        <>
                            <Title level={5} style={{ marginTop: 24 }}>
                                Learning Experiences
                            </Title>

                            {loadingLe ? (
                                <Spin />
                            ) : (
                                <Collapse>
                                    <Collapse.Panel
                                        key="le-root"
                                        header={
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                <span>Learning Experiences</span>
                                                <Checkbox
                                                    checked={isAllLeSelected()}
                                                    indeterminate={isAllLeIndeterminate()}
                                                    onClick={e => e.stopPropagation()}
                                                    onChange={e => toggleAllLe(e.target.checked)}
                                                >
                                                    Select All
                                                </Checkbox>
                                            </div>
                                        }
                                    >
                                        <Collapse>
                                            {les.map(le => (
                                                <Collapse.Panel
                                                    key={le.Key}
                                                    header={
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <span>
                                                                {le.name ?? getLeById(le.Key)?.name ?? le.Key}
                                                            </span>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                                <Checkbox
                                                                    checked={isLeAllSelected(le)}
                                                                    indeterminate={isLeIndeterminate(le)}
                                                                    onClick={e => e.stopPropagation()}
                                                                    onChange={e =>
                                                                        toggleLeAll(le, e.target.checked)
                                                                    }
                                                                >
                                                                    Select All
                                                                </Checkbox>
                                                            </span>
                                                        </div>
                                                    }
                                                >
                                                    <Space direction="vertical">
                                                        <Checkbox
                                                            checked={selectedColumns.includes(
                                                                `${le.Key}_stationsCompleted`
                                                            )}
                                                            onChange={() =>
                                                                toggleColumn(
                                                                    `${le.Key}_stationsCompleted`
                                                                )
                                                            }
                                                        >
                                                            Stations completed
                                                        </Checkbox>

                                                        <Checkbox
                                                            checked={selectedColumns.includes(
                                                                `${le.Key}_totalTime`
                                                            )}
                                                            onChange={() =>
                                                                toggleColumn(
                                                                    `${le.Key}_totalTime`
                                                                )
                                                            }
                                                        >
                                                            Total time
                                                        </Checkbox>

                                                        {Array.from({ length: le.stationCount }).map(
                                                            (_, idx) => {
                                                                const key = `${le.Key}_st${idx}`;
                                                                return (
                                                                    <Checkbox
                                                                        key={key}
                                                                        checked={selectedColumns.includes(key)}
                                                                        onChange={() => toggleColumn(key)}
                                                                    >
                                                                        Station {idx}
                                                                    </Checkbox>
                                                                );
                                                            }
                                                        )}

                                                        {le.hasPracticeProblems && (
                                                            <Checkbox
                                                                checked={selectedColumns.includes(
                                                                    `${le.Key}_extraPractice`
                                                                )}
                                                                onChange={() =>
                                                                    toggleColumn(
                                                                        `${le.Key}_extraPractice`
                                                                    )
                                                                }
                                                            >
                                                                Extra practice
                                                            </Checkbox>
                                                        )}
                                                    </Space>
                                                </Collapse.Panel>
                                            ))}
                                        </Collapse>
                                    </Collapse.Panel>
                                </Collapse>
                            )}
                        </>
                    )}
                </>
            )}

            {step === 2 && (
                <>
                    {selectFilters && loadingSelectUsers && <Spin />}
                    <Checkbox
                        checked={isAllUsersSelected}
                        indeterminate={isIndeterminate}
                        onChange={e => {
                            setIsAllUsersSelected(e.target.checked);
                        }}
                    >
                        Select All Users
                    </Checkbox>

                    <UserSelect
                        users={selectFilters ? selectUsers : (vrUsers ?? [])}
                        value={userIds}
                        disabled={selectFilters ? (loadingSelectUsers || !selectUsers.length) : !vrUsers}
                        handleUserChange={setUserIds}
                    />

                    <UserSelectByEmail
                        multiple
                        users={selectFilters ? selectUsers : (vrUsers ?? [])}
                        value={userEmailIds}
                        disabled={selectFilters ? (loadingSelectUsers || !selectUsers.length) : !vrUsers}
                        handleUserChange={setUserEmailIds}
                    />
                </>
            )}
        </Modal>
    );
};

export default ReportColumnsModal;